'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Longest collapse in the system is 240ms; the net only catches a completion that never lands. */
const SAFETY_MS = 600

/**
 * Height collapses need `overflow: hidden` while they run, but leaving it on afterwards clips
 * anything a child paints outside its border box — our fields draw their 1px ring and 4px focus
 * halo as a box-shadow, so a permanently clipped wrapper eats the left/right edge of every input.
 *
 * Spread the result onto the animating `motion` element: it clips for the duration of the
 * animation and goes back to `visible` the moment it settles. Framer skips the completion
 * callback when a target resolves without animating (a row mounted under `initial={false}`,
 * for one), so a timer releases the clip if `onAnimationComplete` never arrives.
 */
export const useAnimatedClip = () => {
  const [isClipped, setIsClipped] = useState(false)
  const safetyRef = useRef<ReturnType<typeof setTimeout>>()

  const release = useCallback(() => {
    clearTimeout(safetyRef.current)
    setIsClipped(false)
  }, [])

  const onAnimationStart = useCallback(() => {
    clearTimeout(safetyRef.current)
    setIsClipped(true)
    safetyRef.current = setTimeout(() => setIsClipped(false), SAFETY_MS)
  }, [])

  useEffect(() => () => clearTimeout(safetyRef.current), [])

  return useMemo(
    () => ({
      style: { overflow: isClipped ? ('hidden' as const) : ('visible' as const) },
      onAnimationStart,
      onAnimationComplete: release,
    }),
    [isClipped, onAnimationStart, release]
  )
}

export default useAnimatedClip
